import {
  PedagoDashboardHeader,
  PedagogyActionOrigin,
  PedagogyRoom,
  PedagogyRoomUserList,
  PedagogyRoomUserListTab,
  PedagogyUtils,
  ScreenPopup,
  UserCallData,
} from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThanOrEqual, isSuperUser } from "@kalyzee/kast-web-sdk";
import { MeetingSessionRole } from "@kalyzee/kast-web-sdk/http";
import { useRefAutoUpdate, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionHighlightingState, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageMasterMode, usePageContext } from "../../../components/navigation/PageContext";
import { PedagogyMessageForm } from "../../../components/pedagogy/MessageForm";
import { RoomsControlCard, RoomsControlCardData, RoomsControlCardRef } from "../../../components/pedagogy/RoomsControlCard";
import { RoomViewer } from "../../../components/pedagogy/RoomViewer";
import MeetingPlayerStyleContainer from "../../../components/player/container/MeetingPlayerStyleContainer";
import MeetingSessionPlayer from "../../../components/player/MeetingSessionPlayer";
import { LoadingView } from "../../../components/utils/LoadingView";
import { RightLeftArrows } from "../../../components/utils/RightLeftArrows";
import { getEscaetUserCallData, startSessionHighlight } from "../../../helpers/meeting";
import { toastError } from "../../../helpers/toast";
import { DEFAULT_VIDEO_PARAMS } from "../../../helpers/videos";
import { useMeetingConfigurator, useMultiMeetingAPI, useMultiMeetingSelector } from "../../../hooks/meeting";
import { usePedagogyNotifications, usePedagogyUsers } from "../../../hooks/pedagogy";
import { useSettings } from "../../../hooks/settings";
import { EscaetPedagogyAction, MeetingPedagogyUserData } from "../../../interfaces/pedagogy";
import { useUser } from "../../../store/session/hooks";
import { SessionMode } from "../../../store/session/slices";
import { usePedagogyContext } from "./context";
import styles from "./dashboard.module.css";
import { mergeDeep } from "../../../helpers/object";

export const DashboardPedagogyPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const selfUser = useUser();
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const multiMeetingAPI = useMultiMeetingAPI();
  const meetingContainersAPI = useMemo(() => multiMeetingAPI.all?.getContainers()?.map((c) => c.getAPI()) ?? [], [multiMeetingAPI]);
  const roomsControlCardRef = useRef<RoomsControlCardRef>(null);
  const [roomsControlCardData, setRoomsControlCardData] = useState<RoomsControlCardData>([]);
  const [displayAllAgendasButton, setDisplayAllAgendasButton] = useState(false);

  const [meetings] = useMultiMeetingSelector(meetingContainersAPI, (state) => state.meeting);

  const pedaMeetingId = pedagogyCtx?.pedagogyMeetings[0]?.id;

  // All rooms, including the ones with offline meetings that we are not connected to
  const [selectedPedagogyRoom, setSelectedPedagogyRoom] = useState<PedagogyRoom>();
  const [pedagogyRooms, setPedagogyRooms] = useState<PedagogyRoom[]>();
  const [pedagogyRoomUserListTab, setPedagogyRoomUserListTab] = useState<PedagogyRoomUserListTab>("behaviors");

  const [userPopupData, setUserPopupData] = useState<{
    meetingId: string;
    id: string;
    mode: "user" | "session";
    sessionIndex: number;
  }>();

  usePedagogyNotifications(meetingContainersAPI);

  useMeetingConfigurator(meetingContainersAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.extra?.type === "camera") {
            return {
              rtc: mergeDeep(
                {
                  streamParams: {
                    video: {
                      rtcParams: {
                        degradationPreference: "maintain-framerate",
                      },
                      rtcEncoding: {
                        maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.video.maxBitrate,
                        maxFramerate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.video.maxFramerate,
                      },
                    },
                    audio: { mediaContentHint: "speech", rtcEncoding: { maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.webcam.audio.maxBitrate } },
                  },
                },
                settingsRef.current?.debug?.rtc?.pedagoy?.webcam
              ),
            };
          }
          if (mediaSession.extra?.type === "screen" || mediaSession.extra?.type === "board") {
            return {
              rtc: mergeDeep(
                {
                  streamParams: {
                    video: {
                      mediaContentHint: "detail",
                      rtcParams: {
                        degradationPreference: "maintain-resolution",
                      },
                      rtcEncoding: {
                        maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.video.maxBitrate,
                        maxFramerate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.video.maxFramerate,
                      },
                    },
                    audio: { rtcEncoding: { maxBitrate: DEFAULT_VIDEO_PARAMS.pedagogy.screensharing.audio.maxBitrate } },
                  },
                },
                settingsRef.current?.debug?.rtc?.pedagoy?.screensharing
              ),
            };
          }
          return undefined;
        },
      },
      reception: {
        options: () => ({
          audio: false,
          video: { mode: "subscription", delayToDisableStream: 5000 },
        }),
      },
    },
  });

  // peda users
  const pedaUsers = usePedagogyUsers();

  // call users
  const [searchUsersOfMeetingId, setSearchUserOfMeetingId] = useState<string>();
  const searchUsers = useMemo(() => {
    if (!searchUsersOfMeetingId) return undefined;
    const meeting = meetings?.find((m) => m?.id === searchUsersOfMeetingId);
    if (!meeting) return undefined;
    const callData = getEscaetUserCallData(meeting);
    return callData.map((d) => {
      const peda = pedaUsers.find((u) => u.id === d.id);
      if (!peda?.withPedagogy) return d;
      return {
        ...d,
        withPedagogy: true,
      };
    });
  }, [meetings, pedaUsers, searchUsersOfMeetingId]);

  const initialSelectedIds = useMemo(() => pedagogyRooms?.map((room) => room.id), [pedagogyRooms]);

  // ACTIONS OVERWRITES

  const pedagogyRoomUserListActions = useMemo(
    () =>
      pedagogyCtx?.actions.map((a) => {
        let newName: string | ((users: MeetingPedagogyUserData[]) => string) | undefined = undefined;
        if (a.type === "tolerate") newName = "Tolérer et retirer de la liste";
        if (a.type === "warn") newName = pedagogyRoomUserListTab === "behaviors" ? "Avertir" : a.name;
        return newName ? { ...a, name: newName } : a;
      }) ?? [],
    [pedagogyCtx, pedagogyRoomUserListTab]
  );

  const roomViewerPedagogyActions = useMemo(
    () =>
      pedagogyCtx?.actions.map((a) => {
        let newName: string | undefined = undefined;
        // if (a.type === "exclude") newName = "Refuser l'entrée en cours";
        if (a.type === "contact") newName = "Entrer en communication";
        return newName ? { ...a, name: newName } : a;
      }) ?? [],
    [pedagogyCtx]
  );

  const headerContainerRef = useRef<HTMLDivElement>(null);
  useTriggerable(headerContainerRef, { clickCount: 3 }, () => {
    pageContextRef.current?.openSettingsPopup();
  }); // press click 3 times with mouse(left button) on logo

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const seeUserProfile = (meetingId: string, user: UserCallData) => {
    const meeting = meetings?.find((m) => m?.id === meetingId);
    const isUserOnline = meeting?.sessions?.some((s) => s.user?.id === user.id);
    if (!isUserOnline) {
      toastError("L'utilisateur est introuvable.");
      return;
    }
    setSearchUserOfMeetingId(undefined);
    setUserPopupData({ meetingId, mode: "user", id: user.id, sessionIndex: 0 });
  };

  const getSessions = (data: { meetingId: string; id: string; mode: "user" | "session" }) => {
    const meeting = meetings?.find((m) => m?.id === data.meetingId);
    if (!meeting) return [];
    if (data.mode === "user") {
      return meeting?.sessions?.filter((s) => s.user?.id === data.id) ?? [];
    } else {
      const session = meeting?.sessions?.find((s) => s.id === data.id);
      return session ? [session] : [];
    }
  };

  const updatePedagogyRooms = useCallback(() => {
    const data = roomsControlCardRef.current?.getData();
    if (!data) return;
    const pedagogyRooms: PedagogyRoom[] = data?.map((d) => ({
      id: d.room?.id ?? "unknown", // if no meeting, room is disabled so no user shown, id not important
      name: d.room.name,
      isOffline: d.meeting ? false : true,
    }));
    setPedagogyRooms(pedagogyRooms);
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!userPopupData) return;
    const sessions = getSessions(userPopupData);
    const pedaUserId = sessions[0]?.user?.id;
    if (!pedaUserId) return;
    const pedaUser = pedaUsers.find((u) => u.id === pedaUserId);
    if (pedaUser?.withPedagogy) {
      setUserPopupData(undefined);
    }
  }, [userPopupData, pedaUsers]);

  useEffect(() => {
    const validator = pedagogyCtxRef.current?.addAudioValidator(() => false);
    return () => {
      validator?.flush();
    };
  }, []);

  useEffect(() => {
    return () => {
      const ctx = pedagogyCtxRef.current;
      if (!ctx) return;
      const containers = ctx.containersExceptPedagogy;
      ctx.scheduleDestroyContainer(containers);
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const userProfileData = useMemo(() => {
    return {
      firstName: selfUser?.identity?.firstname ?? "",
      lastName: selfUser?.identity?.lastname ?? "",
      profilePicture: selfUser?.identity?.avatar,
    };
  }, [selfUser]);

  if (!pedaMeetingId) return null;

  const renderLeftPanel = () => {
    return (
      <div className={styles.leftPanel}>
        <div ref={headerContainerRef} style={{ width: "100%" }}>
          <PedagoDashboardHeader
            style={{ width: "100%", marginBottom: "10px" }}
            user={userProfileData}
            onExit={() => {
              pageContextRef.current?.goTo(PageMasterMode.LOGOUT);
            }}
          />
        </div>
        {pedagogyRooms ? (
          <PedagogyRoomUserList
            style={{ flex: 1, minHeight: 0, width: "100%", maxWidth: "unset" }}
            users={pedaUsers}
            rooms={pedagogyRooms}
            actions={pedagogyRoomUserListActions}
            initialSelectedRoomIds={initialSelectedIds}
            enabledBadBehaviors={{
              hasBadNetwork: false,
              hasNoCamera: true,
              isNotAttentive: true,
            }}
            defaultTab={pedagogyRoomUserListTab}
            onTab={(tab: PedagogyRoomUserListTab) => setPedagogyRoomUserListTab(tab)}
          />
        ) : (
          <LoadingView dark style={{ flex: 1, minHeight: 0, width: "100%", maxWidth: "unset", zoom: 0.6, background: "#E6E3DD", borderRadius: 8 }} />
        )}
        {roomsControlCardData.find((r) => r.meeting) ? ( // if a meeting exists
          <div style={{ background: "#D6D1C9", padding: "8px", borderRadius: "8px", width: "100%" }}>
            {displayAllAgendasButton ? (
              <PedagogyUtils.CameraDisplayButton
                onClick={() => {
                  roomsControlCardRef.current?.setMode("current-event");
                }}
                style={{ width: "100%" }}
              />
            ) : (
              <PedagogyUtils.CalendarDisplayButton
                onClick={() => {
                  roomsControlCardRef.current?.setMode("agenda");
                }}
                style={{ width: "100%" }}
              />
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div style={{ flex: 1, height: "100%", position: "relative" }}>
        <RoomsControlCard
          ref={roomsControlCardRef}
          className={styles.content}
          onRooms={(rooms) => {
            updatePedagogyRooms();
          }}
          onData={(data) => {
            setRoomsControlCardData(data);
          }}
          onCurrentMeetings={(data) => {
            updatePedagogyRooms();
          }}
          onMode={() => {
            if (!roomsControlCardRef.current) return;
            const allAreInAgenda = roomsControlCardRef.current.allRoomsAreInAgendaMode();
            setDisplayAllAgendasButton(allAreInAgenda);
          }}
          onWaitingRoom={(_, meeting) => {
            const pedaRoom = pedagogyRooms?.find((room) => room.id === meeting.room?.id);
            if (!pedaRoom) return;
            setSelectedPedagogyRoom(pedaRoom);
          }}
          onTalkToExpert={async (_, meeting) => {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT, { id: meeting.id });
          }}
          onJoinMeeting={async (_, meeting) => {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_DASHBOARD, { id: meeting.id });
          }}
          onSupervise={async (_, meeting) => {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: meeting.id });
          }}
          onSearchUsers={(_, meeting) => {
            setSearchUserOfMeetingId(meeting.id);
          }}
          kind="pedagogy"
        />
      </div>
    );
  };

  const renderSessionArrows = () => {
    return (
      <RightLeftArrows
        onClick={(direction) => {
          if (!userPopupData) return;
          const sessions = getSessions(userPopupData);
          if (sessions.length <= 1) return;
          const change = direction === "left" ? -1 : 1;
          const nextIndex = (userPopupData.sessionIndex + change + sessions.length) % sessions.length;
          setUserPopupData({ ...userPopupData, sessionIndex: nextIndex });
        }}
      />
    );
  };

  const renderUserPopup = () => {
    if (!userPopupData) return;

    // confirmation popup displayed
    if (pedagogyCtx.confirmableAction) return;

    const sessions = getSessions(userPopupData);
    if (!sessions.length) {
      setUserPopupData(undefined);
      return null;
    }
    const selectedSessionIdx = Math.min(userPopupData.sessionIndex, sessions.length - 1);
    const selectedSession = sessions[selectedSessionIdx];
    if (!selectedSession) {
      setUserPopupData(undefined);
      return null;
    }
    const meeting = meetings?.find((m) => m?.id === userPopupData.meetingId);

    const user = selectedSession?.user;
    if (!meeting || !selectedSession) return null;

    const meetingAPI = multiMeetingAPI.all
      ?.getContainers()
      ?.find((c) => c.getMeetingId() === meeting.id)
      ?.getAPI();
    const meetingState = meetingAPI?.store?.getState();
    const currentSession = meetingState?.session;
    const receptionSessions = meetingState?.receptionSessions;

    const isMicrophoneActive = selectedSession.medias?.[0]?.audio.enabled ?? false;
    const superUser = isSuperUser(currentSession);
    const currPedaUser = pedaUsers.find((u) => u.id === user?.id);
    const moderator = currentSession ? isMeetingSessionRoleGreaterThanOrEqual(currentSession.role, MeetingSessionRole.MODERATOR) : false;

    const highlighting = selectedSession.highlighting === MeetingSessionHighlightingState.ONGOING;
    const inWaitingRoom = currPedaUser?.message ? true : false;

    const running = meetingState?.meeting?.state === MeetingState.RUNNING;

    const displayExclusionButton = true; // always available
    const disaplayGiveAWarningButton = running;
    const displayAllowEntryIntoClassButton = inWaitingRoom;
    const displayHighlightButton = !inWaitingRoom && !highlighting && moderator && running;
    const displayEnterInCommunicationButton = true; // always available
    const displayNotifyButton = true; // always available

    const executePedagogyAction = (type: EscaetPedagogyAction["type"]) => {
      if (!currPedaUser) return;
      const action = pedagogyCtxRef.current?.actions.find((a) => a.type === type);
      if (!action) return;
      action.onExecute([currPedaUser], PedagogyActionOrigin.Other);
    };

    return (
      <ScreenPopup
        isMicrophoneActive={isMicrophoneActive}
        onToggleMicrophone={
          superUser
            ? async () => {
                if (isMicrophoneActive) meetingAPI?.mute({ sessions: [{ id: selectedSession.id }] });
                else meetingAPI?.unmute({ sessions: [{ id: selectedSession.id }] });
              }
            : undefined
        }
        onExclude={
          displayExclusionButton
            ? async () => {
                executePedagogyAction("exclude");
                setUserPopupData(undefined);
                return false;
              }
            : undefined
        }
        onWriteWarning={
          disaplayGiveAWarningButton
            ? async () => {
                executePedagogyAction("warn");
                return false;
              }
            : undefined
        }
        onAuthorize={
          displayAllowEntryIntoClassButton
            ? async () => {
                executePedagogyAction("tolerate");
                return false;
              }
            : undefined
        }
        onNotify={
          displayNotifyButton
            ? async () => {
                executePedagogyAction("notify");
                return false;
              }
            : undefined
        }
        onEnterInCommunication={
          displayEnterInCommunicationButton
            ? async () => {
                executePedagogyAction("contact");
                return false;
              }
            : undefined
        }
        onBringUpBoard={
          displayHighlightButton
            ? async () => {
                try {
                  startSessionHighlight(selectedSession.id);
                } catch (error) {
                  return false;
                }
                return true;
              }
            : undefined
        }
        onClose={() => {
          setUserPopupData(undefined);
        }}
      >
        <MeetingPlayerStyleContainer mode="dashboard" meeting={meeting} session={selectedSession} style={{ width: "100%", height: "60vh" }}>
          <MeetingSessionPlayer
            style={{ width: "100%", height: "100%" }}
            meeting={meeting}
            sessionId={selectedSession.id}
            ovenMediaSessions={receptionSessions}
            muted={true}
            autoOnOffVideo
          />
          {sessions.length > 1 && renderSessionArrows()}
        </MeetingPlayerStyleContainer>
      </ScreenPopup>
    );
  };

  return (
    <div className={styles.container}>
      {renderLeftPanel()}
      {renderContent()}
      {searchUsers && (
        <PedagogyUtils.UserSearch
          users={searchUsers}
          onUserProfile={(u: UserCallData) => (searchUsersOfMeetingId ? seeUserProfile(searchUsersOfMeetingId, u) : undefined)}
          onClose={() => setSearchUserOfMeetingId(undefined)}
        />
      )}
      {selectedPedagogyRoom && !pedagogyCtx.confirmableAction && (
        <RoomViewer
          pedagogyRoom={selectedPedagogyRoom}
          pedagogyUsers={pedaUsers} // ? withMessage maybe?
          actions={roomViewerPedagogyActions}
          onClose={() => {
            setSelectedPedagogyRoom(undefined);
          }}
        />
      )}
      {/* We come from a full screen popup, the message popup takes the full screen too */}
      <PedagogyMessageForm
        actions={pedagogyCtx?.actions}
        confirmableAction={pedagogyCtx?.confirmableAction}
        onClose={() => {
          pedagogyCtxRef.current?.setConfirmableAction(undefined);
        }}
      />
      {renderUserPopup()}
    </div>
  );
};
